import { useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircleQuestion, X, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '@/lib/utils';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: 'Wie registriere ich mich?',
    answer: 'Klicken Sie auf "Jetzt kostenlos registrieren", geben Sie Ihre E-Mail-Adresse ein und wählen Sie ein sicheres Passwort (mindestens 8 Zeichen mit Groß-/Kleinbuchstaben, Zahl und Sonderzeichen).',
  },
  {
    question: 'Was mache ich, wenn ich mein Passwort vergessen habe?',
    answer: 'Klicken Sie auf der Anmeldeseite auf "Passwort vergessen?" neben dem Passwort-Feld. Geben Sie Ihre E-Mail-Adresse ein und Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.',
  },
  {
    question: 'Wie sicher sind meine Daten?',
    answer: 'Alle Daten werden verschlüsselt übertragen und sicher gespeichert. Ihre Gespräche sind nur für Sie sichtbar.',
  },
  {
    question: 'Kann ich die App auf mehreren Geräten nutzen?',
    answer: 'Ja, melden Sie sich einfach mit Ihrer E-Mail-Adresse und Ihrem Passwort auf dem neuen Gerät an. Ihre Chats werden synchronisiert.',
  },
  {
    question: 'Was ist KI für Pflege?',
    answer: 'KI für Pflege ist eine Chat-Anwendung speziell für Pflegekräfte. Sie können Fragen stellen und erhalten Unterstützung durch künstliche Intelligenz.',
  },
];

export function PublicHelpButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setExpandedIndex(null);
    }
  };

  const handleFAQClick = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const helpContent = (
    <>
      {/* Floating Button */}
      <button
        onClick={handleToggle}
        className={cn(
          'fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'flex items-center justify-center transition-transform',
          isOpen && 'scale-0'
        )}
        aria-label="Hilfe öffnen"
      >
        <MessageCircleQuestion className="h-6 w-6" />
      </button>

      {/* Help Panel */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 z-50 w-80 sm:w-96 max-h-[80vh] bg-background border rounded-lg shadow-xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <MessageCircleQuestion className="h-5 w-5" />
              <h3 className="font-semibold">Hilfe</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-primary-foreground hover:bg-primary/80"
              onClick={handleToggle}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1 overflow-auto">
            <div className="p-3">
              <p className="text-sm text-muted-foreground mb-4">
                Häufig gestellte Fragen zur Anmeldung und Registrierung:
              </p>

              <div className="space-y-2">
                {faqItems.map((item, index) => (
                  <div key={index} className="border rounded-md overflow-hidden">
                    <button
                      onClick={() => handleFAQClick(index)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors"
                    >
                      <span className="text-sm font-medium pr-2">{item.question}</span>
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 flex-shrink-0 transition-transform',
                          expandedIndex === index && 'rotate-90'
                        )}
                      />
                    </button>
                    {expandedIndex === index && (
                      <div className="px-3 pb-3 text-sm text-muted-foreground border-t bg-muted/30">
                        <p className="pt-3">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground mt-4 text-center">
                Nach der Anmeldung steht Ihnen die Chathilfe mit KI-Unterstützung zur Verfügung.
              </p>
            </div>
          </ScrollArea>
        </div>
      )}
    </>
  );

  return createPortal(helpContent, document.body);
}
